<template>
  <div class="dashboard-panel print-break-inside-avoid">
    <h3 class="section-title mb-4">
      <svg class="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6 4h3" />
      </svg>
      Capital Raises Schedule
    </h3>
    
    <div v-if="loading" class="text-center py-4">
      <p class="text-gray-500">Loading capital raises...</p>
    </div>
    
    <div v-else-if="error" class="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4 mb-4">
      <p>{{ error }}</p>
    </div>
    
    <div v-else-if="raises.length === 0" class="text-center py-4">
      <p class="text-gray-500">No capital raises scheduled</p>
    </div>
    
    <div v-else class="overflow-x-auto">
      <table class="w-full text-sm text-left">
        <thead class="table-header">
          <tr>
            <th class="px-3 py-2">Development</th>
            <th class="px-3 py-2">Expense</th>
            <th class="px-3 py-2">Raise</th>
            <th class="px-3 py-2">Month</th>
            <th class="px-3 py-2">Status</th>
            <th class="px-3 py-2 text-right">Amount</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(item, idx) in raises"
            :key="`${idx}-${item.development}-${item.expense_name}-${item.month}-${item.status}`"
            class="border-b border-gray-100 hover:bg-gray-50"
          >
            <td class="px-3 py-2 font-medium">{{ item.development }}</td>
            <td class="px-3 py-2">{{ item.expense_name }}</td>
            <td class="px-3 py-2">{{ item.raise_name || '—' }}</td>
            <td class="px-3 py-2">{{ formatDate(item.month) }}</td>
            <td class="px-3 py-2">
              <span
                class="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold"
                :class="item.status === 'raised'
                  ? 'bg-green-100 text-green-800'
                  : 'bg-amber-100 text-amber-800'"
              >
                {{ item.status === 'raised' ? 'Raised' : 'Still to Raise' }}
              </span>
            </td>
            <td class="px-3 py-2 text-right font-medium">
              {{ formatCurrency(item.amount) }}
            </td>
          </tr>
        </tbody>
        <tfoot v-if="totalAmount > 0" class="bg-gray-50 font-semibold">
          <tr>
            <td colspan="5" class="px-3 py-2 text-right">Total:</td>
            <td class="px-3 py-2 text-right">{{ formatCurrency(totalAmount) }}</td>
          </tr>
        </tfoot>
      </table>
    </div>
    
    <div v-if="lastUpdated" class="mt-2 text-xs text-gray-400 text-right">
      Last updated: {{ formatDate(lastUpdated) }}
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  raises: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
  error: { type: String, default: null },
  lastUpdated: { type: String, default: null }
})

const totalAmount = computed(() =>
  props.raises.reduce((sum, item) => sum + item.amount, 0)
)

const formatDate = (dateString) => {
  if (!dateString) return '-'
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'short' 
  })
}

const formatCurrency = (amount) => {
  if (!amount || amount === 0) return '-'
  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount)
}
</script>
